import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { resetPassword } from '../services/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, formData.newPassword);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Reset Password - Pelican Beaks</title>
        <meta name="description" content="Create a new password for your Pelican Beaks account." />
      </Helmet>
      
      {/* Copied styles from Login.js */}
      <style>
        {`
          @keyframes float {
            0% {
              transform: translate(0, 0) scale(1);
            }
            50% {
              transform: translate(2%, 2%) scale(1.02);
            }
            100% {
              transform: translate(-2%, -2%) scale(0.98);
            }
          }

          .background-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            overflow: hidden;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          .gradient-blob {
            position: absolute;
            border-radius: 100%;
            filter: blur(60px);
            opacity: 0.6;
            animation: float 20s infinite alternate ease-in-out;
          }

          .gradient-blob:nth-child(1) {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            width: 150vw;
            height: 150vw;
            top: -100%;
            left: -25%;
            animation-delay: 0s;
          }

          .gradient-blob:nth-child(2) {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            width: 150vw;
            height: 150vw;
            bottom: -100%;
            right: -25%;
            animation-delay: -5s;
          }

          .gradient-blob:nth-child(3) {
            background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
            width: 100vw;
            height: 100vw;
            top: 30%;
            left: 30%;
            animation-delay: -10s;
          }

          @media (max-width: 768px) {
            .gradient-blob:nth-child(1) {
              width: 200vw;
              height: 200vw;
              top: -150%;
              left: -50%;
            }

            .gradient-blob:nth-child(2) {
              width: 200vw;
              height: 200vw;
              bottom: -150%;
              right: -50%;
            }

            .gradient-blob:nth-child(3) {
              width: 40vw;
              height: 40vw;
              top: 40%;
              left: 30%;
            }
          }
        `}
      </style>

      {/* Animated Gradient Background */}
      <div className="background-container">
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
      </div>

      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(12px)',
              borderRadius: 16
            }}
          >
            <Typography component="h1" variant="h5">
              Create New Password
            </Typography>
            
            {!token && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                Invalid or missing reset token. Please request a new password reset link.
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {error}
              </Alert>
            )}
            
            {success ? (
              <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password reset successful!
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Redirecting you to sign in...
                </Typography>
                <Link component={RouterLink} to="/login" variant="body2">
                  Sign In Now
                </Link>
              </Box>
            ) : token ? (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type="password"
                  id="newPassword"
                  autoComplete="new-password"
                  autoFocus
                  value={formData.newPassword}
                  onChange={handleChange}
                  helperText="Must be at least 6 characters"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Back to Sign In
                  </Link>
                </Box>
              </Box>
            ) : (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Request New Reset Link
                </Link>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default ResetPassword;
